import { useState } from 'react';
import { Check, ChevronsUpDown, Sparkles, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../ui/command';
import { CATEGORY_LABELS, ENTITY_LABELS } from '../../lib/taskTemplatesAPI';

// Searchable task-type picker: "Custom task (manual)" first, then suggested-for-entity, then all by category.
export const TemplateCombobox = ({ templates, value, onChange, entityType }) => {
  const [open, setOpen] = useState(false);
  const selected = templates.find((t) => t.id === value);
  const suggested = entityType ? templates.filter((t) => (t.applicable_entity_types || []).includes(entityType)) : [];
  const groups = Object.keys(CATEGORY_LABELS).map((k) => [k, templates.filter((t) => t.category === k)]).filter(([, l]) => l.length);
  const pick = (id) => { onChange(id); setOpen(false); };
  const Item = ({ t, prefix }) => (
    <CommandItem key={`${prefix}-${t.id}`} value={`${prefix} ${t.name} ${CATEGORY_LABELS[t.category]}`} onSelect={() => pick(t.id)} data-testid={`template-option-${prefix}-${t.id}`}>
      <Check className={cn('w-4 h-4 mr-2', value === t.id ? 'opacity-100' : 'opacity-0')} />{t.name}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid="task-template-combobox">
          <span className="truncate">{selected ? selected.name : 'Custom task (manual)'}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Search task types…" data-testid="task-template-search" />
          <CommandList className="max-h-80">
            <CommandEmpty>No matching template.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="custom task manual" onSelect={() => pick('')} data-testid="template-option-custom">
                <PenLine className="w-4 h-4 mr-2" />Custom task (manual)
              </CommandItem>
            </CommandGroup>
            {suggested.length > 0 && <>
              <CommandSeparator />
              <CommandGroup heading={<span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />Suggested for {ENTITY_LABELS[entityType] || entityType}</span>} data-testid="template-group-suggested">
                {suggested.map((t) => <Item key={`s-${t.id}`} t={t} prefix="suggested" />)}
              </CommandGroup>
            </>}
            {groups.map(([k, list]) => (
              <CommandGroup key={k} heading={`${k}. ${CATEGORY_LABELS[k]}`}>
                {list.map((t) => <Item key={t.id} t={t} prefix={k} />)}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
